import React, { Fragment, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useDroppable } from '@dnd-kit/core';

import CardElementPlaceholder from '../CardElementPlaceholder';
import CameraIcon from '../../../../components/icons/CameraIcon';
import CardElementPhotoControl from './CardElementPhotoControl';
import CardElementPhotoSelectionControl from './CardElementPhotoSelectionControl';
import {
  cardElementPhotoConfigurationHasMinPPI,
  getAutoFitPositionPhotoCropbox,
  getCardPhotoElementConfigurationImageUrl,
  getInitialPhotoCropbox,
} from './helpers';
import CardElementPhotoImage from './CardElementPhotoImage';
import { isCardElementEditable } from '../AccountCard/helpers';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import { isElementCardBackground } from '../../../../components/global/CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';
import { postNativeAppCardsCardElementOpen } from '../../../native-bridge/native-apps-interface';
import useAccountCardNativeFeatureSupport from '../../hooks/useAccountCardNativeFeatureSupport';
import { DROPPABLE_ELEMENT_TYPES } from '../../../../constants/dnd';
import { getMaxThumb } from '../../../../util/photo';
import { PHOTO_SELECTION_CONTROL_FACES_OPTIONS } from './CardElementPhotoSelectionControl/constants';
import { ORIENTATION, PHOTO_TYPE } from '../../constants';
import { shouldUseTeamPhotosSelection } from '../CardElementFrame/helpers';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.shape({
      asset_image: PropTypes.shape({
        id: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
      photo: PropTypes.shape({
        id: PropTypes.string.isRequired,
        is_entitled_matched_photo: PropTypes.bool,
      }),
      cropbox: PropTypes.shape({
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        scale: PropTypes.number.isRequired,
        rotation: PropTypes.number.isRequired,
      }),
    }).isRequired,
    side: PropTypes.string.isRequired,
    loading: PropTypes.bool,
  }).isRequired,
  overlayElement: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
    side: PropTypes.string.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  photosContext: PropTypes.shape({
    account_id: PropTypes.string,
    album_id: PropTypes.string,
    identity_id: PropTypes.string,
    roster_identity_ids: PropTypes.arrayOf(PropTypes.string),
  }),
  accountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  cardWidth: PropTypes.number,
  cardHeight: PropTypes.number,
  printable: PropTypes.bool,
  onPpiWarning: PropTypes.func,
  mappings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      label: PropTypes.string,
      path: PropTypes.string,
    }),
  ),
  allowPhotoChange: PropTypes.bool,
  alwaysAllowPhotoChange: PropTypes.bool,
  disablePhotoWatermarkOverlay: PropTypes.bool,
  defaultPhoto: PropTypes.shape({
    id: PropTypes.string,
  }),
  viewOnly: PropTypes.bool,
  onAddAccountCardElementReference: PropTypes.func,
  allowOrientationChange: PropTypes.bool,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
    watermarkMode: PropTypes.string,
    wallpaperWatermarkMode: PropTypes.string,
  }),
  linkImageElements: PropTypes.bool,
  teamPhotos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      photo: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  ),
};

const CardElementPhoto = ({
  element,
  overlayElement,
  onChange,
  photosContext,
  cardWidth = 7.25,
  cardHeight = 5.25,
  printable,
  onPpiWarning,
  mappings,
  allowPhotoChange = true,
  alwaysAllowPhotoChange = false,
  accountCard,
  disablePhotoWatermarkOverlay,
  defaultPhoto,
  viewOnly,
  onAddAccountCardElementReference,
  allowOrientationChange = false,
  watermarkSettings,
  linkImageElements,
  teamPhotos,
}) => {
  const { cardElement, cardElementConfiguration } = element;
  const {
    photo_url: photoUrl,
    asset_image: assetImage,
    photo,
    photo_type: photoType,
  } = cardElementConfiguration;

  const entity = (assetImage?.id ? assetImage : photo) || defaultPhoto;
  const entityId = entity?.id;

  const [selecting, setSelecting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [latestEntityId, setLatestEntityId] = useState(entityId);
  const cropbox = cardElementConfiguration.cropbox ?? getInitialPhotoCropbox();
  const isDynamicLogo = photoType === PHOTO_TYPE.LOGO;

  // Asset Images are entitled by default, same for photo URLs. Then from there,
  // we want it such that entitlement is determined based off the photo.
  // If the photo does *not* have is_entitled_matched_photo we ignore it.
  // otherwise, account for it.
  const isEntitled =
    disablePhotoWatermarkOverlay ||
    !!assetImage ||
    !!photoUrl ||
    typeof photo?.is_entitled_matched_photo === 'undefined' ||
    !!photo?.is_entitled_matched_photo;

  const imageUrl = getCardPhotoElementConfigurationImageUrl(
    cardElementConfiguration,
    printable ? IMAGE_SIZE_CODE.LARGE2X : IMAGE_SIZE_CODE.LARGE,
    !isEntitled && !printable,
    watermarkSettings,
  );
  const isEditable = useMemo(
    () => alwaysAllowPhotoChange || isCardElementEditable(element),
    [element, alwaysAllowPhotoChange],
  );

  const isLandscape =
    cardWidth * cardElement?.frame?.width >=
    cardHeight * cardElement?.frame?.height;

  // Droppable items. Utilized to allow dropping pictures on a CardElement and updating it.

  const { node, setNodeRef } = useDroppable({
    id: cardElement.id,
    data: {
      type: DROPPABLE_ELEMENT_TYPES.CARD_ELEMENT_PHOTO,
      element,
      onDrop: draggable => {
        if (draggable?.data?.current?.photo) {
          const { photo: draggedPhoto } = draggable.data.current;
          onChange(element, {
            photo_id: draggedPhoto.id,
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth,
              cardHeight,
              thumb: getMaxThumb(draggedPhoto?.thumbnail_urls),
              faceboxes: (draggedPhoto?.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
            }),
          });
        }
      },
    },
  });

  const { nativeSupportsCardElementConfiguration } =
    useAccountCardNativeFeatureSupport();

  const nativeSupportsCardElementConfigurationBool =
    nativeSupportsCardElementConfiguration(element.cardElement.type);

  const onAdjustAndCrop = () => {
    if (!isEditable || viewOnly) {
      return;
    }

    if (shouldUseTeamPhotosSelectionBool) {
      setSelecting(true);
    } else if (assetImage || photo) {
      setEditing(true);
    } else if (nativeSupportsCardElementConfigurationBool) {
      postNativeAppCardsCardElementOpen({
        accountCardId: accountCard.id,
        cardElementId: element.cardElement.id,
        cardElementType: element.cardElement.type,
        cardElementConfiguration: cardElementConfiguration?.id
          ? cardElementConfiguration
          : null,
      });
    } else {
      setSelecting(true);
    }
  };

  // When the currently configured photo is one of the team photos and there
  // is more than one to choose from, force the photo-selection control to be
  // used (with team photos as the source) instead of the editing/cropping UI.
  // Limit this to non-editable elements as there we would've auto-populated
  // the photo and disabled editing via code.
  const shouldUseTeamPhotosSelectionBool =
    !alwaysAllowPhotoChange &&
    shouldUseTeamPhotosSelection(element, teamPhotos);

  const onPhotoChange = () => {
    if (nativeSupportsCardElementConfigurationBool) {
      postNativeAppCardsCardElementOpen({
        accountCardId: accountCard.id,
        cardElementId: element.cardElement.id,
        cardElementType: element.cardElement.type,
        cardElementConfiguration: cardElementConfiguration?.id
          ? cardElementConfiguration
          : null,
      });
    } else {
      setSelecting(true);
    }

    setEditing(false);
  };

  useEffect(() => {
    // In case a photo gets removed we don't open the editor.
    // We only do this on native because on Web we have the initial photo auto-fill in which we
    // do all the work outside of this component anyway.
    if (!element?.loading) {
      if (latestEntityId !== entityId && isEditable && !viewOnly) {
        if (entityId) {
          const currentOrientation =
            cardHeight > cardWidth
              ? ORIENTATION.PORTRAIT
              : ORIENTATION.LANDSCAPE;

          const newOrientation = (() => {
            if (assetImage && entity === assetImage) {
              return assetImage.height > assetImage.width
                ? ORIENTATION.PORTRAIT
                : ORIENTATION.LANDSCAPE;
            }

            const maxThumb = getMaxThumb(entity?.thumbnail_urls);

            return maxThumb.height > maxThumb.width
              ? ORIENTATION.PORTRAIT
              : ORIENTATION.LANDSCAPE;
          })();

          // Only update orientation if the photo is full frame.
          // Otherwise we might be using a non-intended rotation on a
          // custom print photo frame, which would not make sense.
          const shouldUpdateOrientation =
            allowOrientationChange &&
            cardElement?.frame?.width === 1 &&
            cardElement?.frame?.height === 1 &&
            newOrientation !== currentOrientation;

          onChange(element, {
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth: shouldUpdateOrientation ? cardHeight : cardWidth,
              cardHeight: shouldUpdateOrientation ? cardWidth : cardHeight,
              thumb:
                assetImage && entity === assetImage
                  ? entity
                  : getMaxThumb(entity?.thumbnail_urls),
              faceboxes: (entity?.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
            }),
            orientation: shouldUpdateOrientation ? newOrientation : undefined,
          });

          if (!linkImageElements) {
            setEditing(true);
          }
        }

        setLatestEntityId(entityId);
      }
    }
  }, [entityId, element?.loading]);

  useEffect(() => {
    if (node && isEditable && onAddAccountCardElementReference) {
      onAddAccountCardElementReference?.({
        cardElementId: cardElement.id,
        ref: {
          node,
          onAdjustAndCrop,
          onPhotoChange: !allowPhotoChange ? undefined : onPhotoChange,
        },
      });
    }
  }, [node, element]);

  return (
    <Fragment>
      <a
        href="#"
        ref={setNodeRef}
        className={classNames({
          'card-element card-element-photo': true,
          'card-element-photo-background': isElementCardBackground(element),
          'card-element-photo-logo': isDynamicLogo,
          // Prevents the Overlay hack from affecting photo placement
          'card-element-not-editable': !isEditable && !photo?.id,
          'card-element-hidden': !isEditable && !photo?.id && !assetImage?.id,
          'card-element-loading': element.loading,
        })}
        onClick={event => {
          event.preventDefault();
          if (shouldUseTeamPhotosSelectionBool) {
            setSelecting(true);
          } else {
            onAdjustAndCrop();
          }
        }}
      >
        {imageUrl ? (
          <CardElementPhotoImage
            cropbox={cropbox}
            src={imageUrl}
            isEntitled={isEntitled}
            isForegroundImage={!!photo?.foreground?.original_url}
            isWallpaperImage={
              imageUrl === photo?.wallpaper_watermark_urls?.[0]?.url
            }
            watermarkSettings={watermarkSettings}
          />
        ) : (
          <CardElementPlaceholder
            iconClassName="card-element-placeholder-photo-icon"
            icon={
              <div className="d-flex flex-column align-items-center text-xs">
                <CameraIcon
                  color="#0d84fe"
                  id={`camera-icon-for-${element.cardElement?.id}`}
                />
                <div className="mt-1 text-center px-2">Tap to Select Photo</div>
              </div>
            }
          />
        )}
      </a>

      <CardElementPhotoSelectionControl
        // TODO [cards] Highlight the configured photo / image here.
        forceOpen={selecting}
        onClose={() => {
          setSelecting(false);
        }}
        onChange={value => {
          // Note that `value` here can be either an AssetImage (just uploaded)
          // or a Photo.

          // Change nothing if the user has not made a change.
          if (value.id === photo?.id || value.id === assetImage?.id) {
            if (!shouldUseTeamPhotosSelectionBool) {
              setEditing(true);
            }
            return;
          }

          // When using the team-photos selection mode, swap the photo and
          // auto-fit it without opening the cropping editor.
          if (shouldUseTeamPhotosSelectionBool) {
            onChange(element, {
              asset_image_id: null,
              photo_id: value.id,
              cropbox: getAutoFitPositionPhotoCropbox({
                cardElement,
                cardWidth,
                cardHeight,
                thumb: getMaxThumb(value?.thumbnail_urls),
                faceboxes: (value?.faces?.records ?? []).map(
                  ({ facebox }) => facebox,
                ),
              }),
            });
            return;
          }

          const config = {};

          if (value.thumbnail_urls) {
            config.asset_image_id = null;
            config.photo_id = value.id;
          } else {
            config.asset_image_id = value.id;
            config.photo_id = null;
          }

          onChange(element, config);

          setEditing(true);
        }}
        photosContext={photosContext}
        mappings={mappings}
        facesOptionDefault={
          isLandscape
            ? PHOTO_SELECTION_CONTROL_FACES_OPTIONS.MULTIPLE
            : PHOTO_SELECTION_CONTROL_FACES_OPTIONS.INDIVIDUAL
        }
        watermarkSettings={watermarkSettings}
        teamPhotos={shouldUseTeamPhotosSelectionBool ? teamPhotos : undefined}
        currentSelectedPhoto={
          shouldUseTeamPhotosSelectionBool ? photo : undefined
        }
      />

      <CardElementPhotoControl
        forceOpen={editing}
        onClose={() => setEditing(false)}
        onChange={newCropbox => {
          onChange(element, {
            cropbox: {
              x: newCropbox.x,
              y: newCropbox.y,
              scale: newCropbox.scale,
              rotation: newCropbox.rotation,
            },
          });

          if (
            !cardElementPhotoConfigurationHasMinPPI({
              element: {
                ...element,
                cardElementConfiguration: {
                  ...element.cardElementConfiguration,
                  cropbox: newCropbox,
                },
              },
              cardWidth,
              cardHeight,
              photo,
            })
          ) {
            onPpiWarning?.();
          }
        }}
        onPhotoChange={!allowPhotoChange ? undefined : onPhotoChange}
        element={element}
        overlayingElement={overlayElement}
        cardWidth={cardWidth}
        cardHeight={cardHeight}
        isEntitled={isEntitled}
        watermarkSettings={watermarkSettings}
      />
    </Fragment>
  );
};

CardElementPhoto.propTypes = propTypes;

export default CardElementPhoto;
